import type { LandingContent } from './landingContentTypes.js';
import { globusCampaignContent } from './landingContentGlobusCampaign.js';
import * as shared from './landingContentShared.js';
const { industryCare, industryCraft, industryRetail, heroPflege, heroHandwerk, heroHandel, heroUnternehmenBedarf, heroUnternehmenBranchen, heroUnternehmenDokumente, heroUnternehmenIntegration, partnerGlobus, partnerTempusleben, partnerEmek, teamGerdSchneider, groupPhoto, contact } = shared;
import { deContent } from './landingContent.de.js';
import { enContent } from './landingContent.en-base.js';

export const activeEnContent: LandingContent = {
  ...enContent,
  locale: 'en',
  languageLabel: 'English',
  nav: [
    { label: 'Care', href: '/branchen/pflege' },
    { label: 'Retail', href: '/branchen/einzelhandel' },
    { label: 'Trades', href: '/branchen/handwerk' },
  ],
  heroFanApplicant: {
    badge: 'For international applicants',
    avatars: [industryCare, industryRetail, industryCraft],
    title: 'Your professional start in Germany begins here.',
    highlight: 'begins here.',
    intro: 'You want to work in Germany and need a serious path forward? Viva Porta shows you suitable industries, explains the next steps and personally supports you from orientation to arrival.',
    trustLabel: '',
    cards: [
      {
        eyebrow: 'Care',
        title: 'Start as a care professional',
        image: heroPflege,
        imageAlt: 'Care professional in a bright modern facility',
        href: '/branchen/pflege',
      },
      {
        eyebrow: 'Retail',
        title: 'Start in retail with Globus',
        image: heroHandel,
        imageAlt: 'Employee in a modern retail store',
        href: '/branchen/einzelhandel',
      },
      {
        eyebrow: 'Trades',
        title: 'Build your path in trades',
        image: heroHandwerk,
        imageAlt: 'Skilled worker in a bright workshop',
        href: '/branchen/handwerk',
      },
    ],
  },
  heroFanCompany: {
    badge: 'For employers in Germany',
    avatars: [industryCare, industryRetail, industryCraft],
    title: 'Hire international people who truly fit your team.',
    highlight: 'fit your team.',
    intro: 'Looking for reliable international staff? Viva Porta clarifies your demand, checks profiles, coordinates formalities and supports arrival in your operation.',
    trustLabel: '',
    cards: [
      {
        eyebrow: 'Demand',
        title: 'Clarify staffing needs',
        image: heroUnternehmenBedarf,
        imageAlt: 'Consultation meeting with an employer',
        href: '#kontakt',
        quote: 'Tell us about your open roles. Together, we assess your needs, timeline and realistic next steps.',
        quoteAuthor: 'Clarify staffing needs',
        quoteRole: 'Discuss your needs',
      },
      {
        eyebrow: 'Industries',
        title: 'Profiles for real roles',
        image: heroUnternehmenBranchen,
        imageAlt: 'Team working in a modern operation',
        href: '#branchen',
        quote: 'Explore our industry solutions and see how we prepare suitable profiles for real responsibilities.',
        quoteAuthor: 'Find suitable profiles',
        quoteRole: 'View industries',
      },
      {
        eyebrow: 'Formalities',
        title: 'Documents coordinated',
        image: heroUnternehmenDokumente,
        imageAlt: 'Documents and digital planning for international hiring',
        href: '#ablauf',
        quote: 'See how selection, documents, entry and the first day at work are coordinated transparently.',
        quoteAuthor: 'Plan with confidence',
        quoteRole: 'View process',
      },
      {
        eyebrow: 'Integration',
        title: 'Support arrival in the team',
        image: heroUnternehmenIntegration,
        imageAlt: 'International professional welcomed into a team',
        href: '#kontakt',
        quote: 'Let’s discuss how preparation, onboarding and personal guidance can strengthen your team for the long term.',
        quoteAuthor: 'Shape a successful arrival',
        quoteRole: 'Get in touch',
      },
    ],
  },
  globusCampaignApplicant: globusCampaignContent.en.applicant,
  globusCampaignCompany: globusCampaignContent.en.company,
  intro: {
    eyebrow: 'About Viva Porta',
    title: 'Your path to Germany, clearly guided and personally supported.',
    titleHighlight: 'supported',
    text: 'We combine Viva Porta’s experience in visa and relocation processes with in-depth knowledge of international career pathways. The result is a structured path for people who want to build a professional future in Germany.',
    metrics: [
      { value: '360°', label: 'Support for language, recognition, visa and arrival' },
      { value: '2', label: 'Two cultures, one team: Germany and Türkiye' },
      { value: '24/7', label: 'Reachable support in critical phases' },
    ],
  },
  industryOverview: {
    eyebrow: 'Industries',
    title: 'Not just recruitment: we bring the right people to you.',
    titleHighlight: 'right people',
    text: 'Viva Porta connects companies and international applicants individually, personally and exclusively. We do not only look at qualifications, but at people, teams, expectations and whether collaboration can truly last.',
    items: [
      {
        iconClass: 'icon-[material-symbols--medical-services]',
        title: 'Care',
        text: 'For clinics, care facilities and outpatient services that want to integrate qualified care professionals long term.',
        href: '/branchen/pflege',
        linkLabel: 'View care',
      },
      {
        iconClass: 'icon-[material-symbols--storefront]',
        title: 'Retail',
        text: 'For Globus, fresh-food counters, sales and service-oriented teams with lasting staffing needs.',
        href: '/branchen/einzelhandel',
        linkLabel: 'View retail',
      },
      {
        iconClass: 'icon-[material-symbols--construction]',
        title: 'Trades',
        text: 'For trade businesses that want to solve junior and skilled-worker shortages in a structured, human way.',
        href: '/branchen/handwerk',
        linkLabel: 'View trades',
      },
    ],
  },
  laborShortage: {
    ...deContent.laborShortage,
    eyebrow: 'Understanding the skills shortage',
    title: 'When open positions become a risk',
    titleHighlight: 'risk',
    text: 'Our mission is not to turn staffing shortages into short-term business. Viva Porta wants to solve a social problem: companies need reliable people, international applicants need fair opportunities, and both sides need guidance that remains human.',
    stats: [
      {
        value: '3.2 million',
        label: 'fewer people of working age by the mid-2030s, even with high net immigration.',
        sourceLabel: 'Destatis, 2025',
        sourceHref: deContent.laborShortage.stats[0]?.sourceHref ?? '',
      },
      {
        value: '400,000',
        label: 'annual net immigration estimated by the IAB to keep labour supply approximately stable over the long term.',
        sourceLabel: 'IAB',
        sourceHref: deContent.laborShortage.stats[1]?.sourceHref ?? '',
      },
      {
        value: '768,000',
        label: 'positions that may remain unfilled by qualified workers by 2028 if supply and demand continue to diverge, according to the IW outlook.',
        sourceLabel: 'IW / iwd',
        sourceHref: deContent.laborShortage.stats[2]?.sourceHref ?? '',
      },
    ],
    points: [
      {
        iconClass: 'icon-[material-symbols--volunteer-activism]',
        title: 'What is our mission?',
        text: 'We want to stabilise care, trades, retail and teams without reducing people to CVs or vacancies. Placement only makes sense to us when it creates a real perspective.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-1]',
        title: 'Where does our approach come from?',
        text: 'Enes, Serife and Murat bring a strong social and non-profit background: closeness to people, understanding of integration and responsibility shape the work. Kirstin and Ergün add everyday experience in guidance and organisation.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Why do people like Wilhelm invest?',
        text: 'Wilhelm supports Viva Porta because recruitment here is not treated as a pure business model. It is about building a structure that helps companies and enables people to make a fair, long-term start.',
      },
    ],
    sources: [
      { label: 'Federal Employment Agency: Bottleneck analysis', href: deContent.laborShortage.sources[0]?.href ?? '' },
      { label: 'Destatis: Demographic change', href: deContent.laborShortage.sources[1]?.href ?? '' },
      { label: 'IAB: Labour supply and immigration', href: deContent.laborShortage.sources[2]?.href ?? '' },
      { label: 'IW: Skills gap continues to grow', href: deContent.laborShortage.sources[3]?.href ?? '' },
    ],
  },
  process: {
    eyebrow: 'How we work together',
    title: 'A clear process from demand to arrival.',
    titleHighlight: 'arrival',
    text: 'International recruitment needs structure. Viva Porta makes every step understandable: from the first conversation through selection, preparation and formalities to integration into everyday work.',
    steps: [
      {
        iconClass: 'icon-[material-symbols--fact-check]',
        title: 'Understand the need',
        text: 'We clarify industry, role, qualification, start date, language, working conditions and integration options.',
      },
      {
        iconClass: 'icon-[material-symbols--manage-search]',
        title: 'Find suitable candidates',
        text: 'Profiles are prequalified professionally, linguistically and personally so companies can make informed decisions.',
      },
      {
        iconClass: 'icon-[material-symbols--school]',
        title: 'Guide preparation',
        text: 'Language, role understanding, expectations and necessary qualification steps are structured early.',
      },
      {
        iconClass: 'icon-[material-symbols--approval-delegation]',
        title: 'Support formalities',
        text: 'Depending on the industry, we coordinate documents, recognition, visa, work permit and relevant evidence.',
      },
      {
        iconClass: 'icon-[material-symbols--flight-land]',
        title: 'Enable arrival',
        text: 'Entry, first orientation, accommodation and communication with the employer are prepared in a plannable way.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Stay involved long term',
        text: 'We remain available so a placement can become stable collaboration for both sides.',
      },
    ],
  },
  values: {
    eyebrow: 'Mission',
    title: 'Our mission is a human answer to a structural problem.',
    titleHighlight: 'human answer',
    text: 'Viva Porta wants to meaningfully connect Germany’s skills shortage with the motivation of international people. We see ourselves as a bridge between companies and people who want to build their professional life here.',
    items: [
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Trust',
        text: 'Clear communication, transparent steps and fixed contacts create security on both sides.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-1]',
        title: 'People at the centre',
        text: 'Behind every staffing need and every application are people with expectations, responsibility and plans for the future.',
      },
      {
        iconClass: 'icon-[material-symbols--verified-user]',
        title: 'Sustainability',
        text: 'Good placement does not end with a contract. It proves itself when collaboration lasts.',
      },
    ],
    bridgeTitle: 'Why Viva Porta as a bridge?',
    bridgeText:
      'Germany needs reliable professionals. At the same time, many people internationally want to work, learn and arrive here. Viva Porta brings these interests together personally, fairly and with long-term integration in mind.',
    bridgeAction: { label: 'Why people are at the centre', href: '/ueber-vivaporta' },
  },
  story: {
    ...deContent.story,
    eyebrow: 'Voices from our work',
    title: 'Real experiences instead of interchangeable references.',
    titleHighlight: 'experiences',
    text: 'Approved feedback from companies, applicants and partners can be managed in the editor with image, logo and context.',
    quote: 'A real customer or candidate quote can appear here. This area is prepared so later voices can be managed credibly and completely in the editor.',
    author: 'Placeholder for real testimonial',
    role: 'Please replace in the editor with an approved quote',
    testimonials: [
      {
        quote: 'Viva Porta is an exceptionally committed business partner with outstanding connections to the Turkish labour market.',
        author: 'Gerd Schneider',
        role: 'Managing Director, tempusleben',
        image: teamGerdSchneider,
        imageAlt: 'Gerd Schneider, Managing Director of tempusleben',
        logo: partnerTempusleben,
        logoAlt: 'tempusleben logo',
      },
      {
        quote: 'Good collaboration begins where people are prepared, understood and reliably supported all the way into everyday work.',
        author: 'Viva Porta partner network',
        role: 'Preparation and integration',
        image: heroUnternehmenIntegration,
        imageAlt: 'International professional being welcomed into the team',
        logo: partnerEmek,
        logoAlt: 'EMEK logo',
      },
      {
        quote: 'In retail, qualification, pace and team fit have to come together. For Globus we prepare specialists specifically for roles in fresh-food departments and service.',
        author: 'Viva Porta × Globus',
        role: 'Retail partnership',
        image: heroHandel,
        imageAlt: 'Employee in a modern retail store',
        logo: partnerGlobus,
        logoAlt: 'Globus logo',
      },
    ],
  },
  partners: {
    eyebrow: 'Partners & network',
    title: 'Trust built on real partnerships.',
    titleHighlight: 'partnerships',
    text: 'Selected companies and networks in Germany and Türkiye that support our work.',
    trustLabel: 'Member of the GVP – German Federation of Staffing Providers',
    items: [
      {
        name: 'Globus',
        logo: partnerGlobus,
        alt: 'Globus logo',
        description: 'For full-range retailers like Globus, we recruit specialists for fresh-food departments: butchery, fish and cheese. Exactly where advice drives footfall, enables margins and builds lasting customer loyalty.',
        href: '/globus',
      },
      {
        name: 'tempusleben',
        logo: partnerTempusleben,
        alt: 'tempusleben logo',
        description: 'tempusleben is an outpatient and intensive-care nursing provider. The partnership shows how important reliable, human-guided recruitment is in sensitive care settings.',
        href: 'https://tempusleben.de/',
      },
      {
        name: 'EMEK',
        logo: partnerEmek,
        alt: 'EMEK logo',
        description: 'EMEK GmbH is a care provider. As a partner from the care environment, EMEK stands for practical understanding, closeness to everyday work and a network that prepares people instead of merely placing them.',
        href: 'https://emek-pflegedienst.de/',
      },
    ],
  },
  globusPartner: {
    eyebrow: 'Retail partner',
    title: 'A new start in retail with Globus.',
    text: 'Globus stands for large, service-oriented stores with strong fresh-food departments. Together we create a framework in which international talent can be prepared, guided and integrated into teams.',
    image: groupPhoto,
    imageAlt: 'Viva Porta visiting the butchery department of a GLOBUS store',
    logo: partnerGlobus,
    logoAlt: 'Globus logo',
    primaryAction: { label: 'View retail', href: '/branchen/einzelhandel' },
  },
  info: {
    eyebrow: 'Additional information',
    title: 'Important resources about immigration, recognition and working in Germany.',
    titleHighlight: 'resources',
    links: [
      { label: 'Recognition in Germany', href: deContent.info.links[0]?.href ?? '' },
      { label: 'Make it in Germany', href: deContent.info.links[1]?.href ?? '' },
      { label: 'Federal Employment Agency', href: deContent.info.links[2]?.href ?? '' },
    ],
  },
  quality: {
    eyebrow: 'Quality and integration',
    title: 'Fair placement that arrives sustainably in the team.',
    titleHighlight: 'sustainably',
    text: 'For us, successful placement does not end at the airport. Verified quality and real integration help new employees stay long term.',
    stats: [
      { value: 'Long term', label: 'Collaboration focused on stable perspectives' },
      { value: 'Prepared', label: 'Language and professional assessment before the start' },
      { value: 'Guided', label: 'Reachable support during important arrival phases' },
    ],
    points: [
      {
        iconClass: 'icon-[material-symbols--verified]',
        title: 'Fair recruitment',
        text: 'We follow recognised principles of responsible recruitment and value transparent, understandable processes.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-3]',
        title: 'Cultural integration',
        text: 'Intercultural preparation and personal support help professionals settle into the team and everyday life.',
      },
      {
        iconClass: 'icon-[material-symbols--health-and-safety]',
        title: 'Quality checked',
        text: 'Professional suitability, language certificate and recognition procedures are documented transparently before placement.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Long-term retention',
        text: 'Aftercare and onboarding support reduce turnover and secure the lasting success of hiring.',
      },
    ],
  },
  faqCompany: {
    eyebrow: 'FAQ for employers',
    title: 'Answers about international recruitment.',
    titleHighlight: 'Answers',
    text: 'If you have questions about process, expectations or suitable profiles, speak directly with our team.',
    items: [
      {
        question: 'Which industries do you focus on?',
        answer: 'Viva Porta currently focuses on care, retail and trades. Role, qualification and integration needs are assessed separately for each industry.',
      },
      {
        question: 'How are candidates prequalified?',
        answer: 'Professional experience, language status, documents, motivation and role fit are assessed in a structured way.',
      },
      {
        question: 'Do you support formalities?',
        answer: 'Yes. Depending on the industry, we support the coordination of documents, recognition, visa, work permit and relevant evidence.',
      },
      {
        question: 'How long does the process take?',
        answer: 'That depends on the role, language level and formalities. At the start, we provide a realistic timeline and clear next steps.',
      },
      {
        question: 'Do you stay involved after the start?',
        answer: 'Yes. We remain available during onboarding, communication and integration phases so collaboration can become stable.',
      },
    ],
  },
  contact: {
    eyebrow: 'Ready for the next step?',
    title: 'Apply for Germany free of charge.',
    titleHighlight: 'Germany',
    text: 'Send us your request. We will respond with a realistic assessment and the next steps for your path.',
    primaryAction: { label: 'Apply for free', href: contact.application },
    secondaryAction: { label: 'Send email', href: contact.mail },
    address: 'Viva Porta GmbH, Germany',
    email: 'info@vivaporta.org',
    hours: 'Mon - Fri: 9:00 - 17:00',
  },
  footer: {
    text: 'Viva Porta connects companies in Germany with international people who want to build their professional life here. Personal, serious and designed for the long term.',
    links: [
      { label: 'Apply now', href: '/bewerben' },
      { label: 'Work at Globus', href: '/globus' },
      { label: 'Our team', href: '/team' },
      { label: 'Mission', href: '/ueber-vivaporta' },
      { label: 'Contact', href: '/kontakt' },
      { label: 'Home', href: '/' },
      { label: 'Care', href: '/branchen/pflege' },
      { label: 'Retail', href: '/branchen/einzelhandel' },
      { label: 'Trades', href: '/branchen/handwerk' },
      { label: 'Legal notice', href: '/impressum' },
    ],
    socialLinks: deContent.footer.socialLinks,
  },
};
